import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Helmet } from 'react-helmet-async';

import PageHeader from './PageHeader';
import { SITE_URL } from '../../lib/seo';

// ─────────────────────────────────────────────────────────────
// Architecture — Al-Amen platform technical stack.
// Six layers: presentation → gateway → vision/OCR → language →
// reasoning → data. Locale-driven via architecture.layers.*.
// ─────────────────────────────────────────────────────────────

const LAYER_ORDER = [
  'presentation',
  'gateway',
  'vision',
  'language',
  'reasoning',
  'data'
];

function parseLayer(raw) {
  const text = typeof raw === 'string' ? raw : '';
  const sep = text.includes(' — ') ? ' — ' : text.includes(' - ') ? ' - ' : null;
  if (!sep) return { name: text, body: '' };
  const [name, ...rest] = text.split(sep);
  return { name: name.trim(), body: rest.join(sep).trim() };
}

function LayerConnector() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: 'text.secondary',
        my: 1
      }}
    >
      <Box
        sx={{
          width: '1px',
          height: 28,
          bgcolor: 'divider'
        }}
      />
      <KeyboardArrowDownRoundedIcon fontSize="small" sx={{ opacity: 0.7 }} />
    </Box>
  );
}

function LayerCard({ id, num, name, body, index }) {
  const even = index % 2 === 0;

  return (
    <Paper
      variant="outlined"
      id={id}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: even ? 'background.paper' : 'background.default',
        scrollMarginTop: 96
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 3 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{
          px: { xs: 3, md: 4 },
          py: { xs: 2.5, md: 3 },
          borderBottom: body ? '1px solid' : 'none',
          borderColor: 'divider'
        }}
      >
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.78rem',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: 'primary.main'
          }}
        >
          Layer {num}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'var(--font-serif)',
            fontSize: { xs: '1.6rem', md: '2rem' },
            letterSpacing: '-0.015em',
            lineHeight: 1.1,
            flex: 1
          }}
        >
          {name}
        </Typography>
      </Stack>

      {body ? (
        <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 2.5, md: 3 } }}>
          <Typography sx={{ color: 'text.secondary', maxWidth: 900, fontSize: '1.02rem', lineHeight: 1.6 }}>
            {body}
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
}

export default function Architecture() {
  const { t } = useTranslation();
  const layers = LAYER_ORDER.map((id, index) => {
    const parsed = parseLayer(t(`architecture.layers.${id}`));
    return {
      id,
      num: String(index + 1).padStart(2, '0'),
      name: parsed.name,
      body: parsed.body
    };
  });

  return (
    <Box>
      <Helmet>
        <title>Architecture | Al-Amen Technology</title>
        <link rel="canonical" href={`${SITE_URL}/architecture`} />
      </Helmet>
      <PageHeader
        eyebrow={t('architecture.eyebrow')}
        title={t('architecture.title')}
        body={t('architecture.body')}
      />

      <Box component="section" sx={{ bgcolor: 'background.paper' }}>
        <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 4 } }}>
          <Paper
            variant="outlined"
            role="note"
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 2,
              borderStyle: 'dashed',
              bgcolor: 'background.default'
            }}
          >
            <Typography sx={{ color: 'text.secondary', fontSize: '0.92rem', lineHeight: 1.6 }}>
              {t('architecture.note')}
            </Typography>
          </Paper>
        </Container>
      </Box>

      <Box component="section" sx={{ py: { xs: 5, md: 8 } }}>
        <Container maxWidth="xl">
          <Stack spacing={0} alignItems="stretch">
            {layers.map((layer, idx) => (
              <React.Fragment key={layer.id}>
                <LayerCard {...layer} index={idx} />
                {idx < layers.length - 1 ? <LayerConnector /> : null}
              </React.Fragment>
            ))}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
